import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { addToCart, getToken, removeToken, removeUser, addToWishlist, removeFromWishlist, getWishlist } from '../api/auth';
import { Heart, ShoppingBag } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ── Badge colour map ─────────────────────────────────────── */
const BADGE_STYLES = {
  'new':        { bg: '#1C1A18', color: '#fff' },
  'bestseller': { bg: '#B08D57', color: '#fff' },
  'trending':   { bg: '#7B4F2E', color: '#fff' },
  'limited':    { bg: '#C0392B', color: '#fff' },
  'sale':       { bg: '#C0392B', color: '#fff' },
  'featured':   { bg: '#4A6741', color: '#fff' },
  default:      { bg: '#1C1A18', color: '#fff' },
};

const getBadgeStyle = (badge) => {
  if (!badge) return null;
  const key = badge.toLowerCase().replace(/\s+/g, '');
  return BADGE_STYLES[key] || BADGE_STYLES.default;
};

/* ── Get Product Images ─────────────────────────────── */
const getProductImages = (product) => {
  const fields = [product.image_1, product.image_2, product.image_3].filter(Boolean);
  const jsonImages = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  return [...new Set([...fields, ...jsonImages])];
};

/* ── Image Swiper ────────────────────────────────── */
const ImageSwiper = ({ product }) => {
  const imgs = getProductImages(product);
  const count = imgs.length;
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);

  const go = (dir, e) => {
    e.stopPropagation();
    setCurrent(c => (c + dir + count) % count);
  };

  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].screenX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1, e);
    touchStartX.current = null;
  };

  return (
    <div className="rpp-slide-wrap" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="rpp-slide-strip" style={{ transform: `translateX(-${current * 100}%)` }}>
        {imgs.map((src, idx) => (
          <img key={idx} src={src} alt={`${product.name} ${idx + 1}`} loading="lazy" />
        ))}
      </div>
      {count > 1 && (
        <>
          <button className="rpp-sarrow rpp-sarrow-l" onClick={e => go(-1, e)} aria-label="Previous image">←</button>
          <button className="rpp-sarrow rpp-sarrow-r" onClick={e => go(1, e)} aria-label="Next image">→</button>
        </>
      )}
    </div>
  );
};

/* ── Toast ────────────────────────────────────────────────── */
const Toast = ({ msg, show }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        className="rpp-toast"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.28 }}
      >
        ✓ {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ── Product Card ─────────────────────────────────────────── */
const ProductCard = ({ product, reelId, index, onToast, wished, onToggleWishlist }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [added, setAdded] = useState(false);
   console.log(product.name, '| colors:', product.colors, '| sizes:', product.sizes);

  const handleAdd = async (e) => {
  e.stopPropagation();
  if (!getToken()) { navigate('/account'); return; }

  const colorCount = [...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length;
  const sizeCount  = [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length;

  if (colorCount > 1 || sizeCount > 1) {
    navigate(`/reels/${reelId}/${product.id}`)  
    return;
  }

  try {
    await addToCart(product.id, 1);
    setAdded(true);
    onToast(`${product.name} added to cart!`);
    setTimeout(() => setAdded(false), 1800);
    window.dispatchEvent(new Event('cart-updated'));
  } catch (err) {
    const msg = err.message?.toLowerCase() || '';
    if (msg.includes('invalid') || msg.includes('expired') || msg.includes('session') || msg.includes('token') || msg.includes('unauthorized')) {
      removeToken(); removeUser(); navigate('/account'); return;
    }
    onToast(err.message || 'Failed to add to cart');
  }
};

  const off = product.sale_price
    ? Math.round(((product.original_price - product.sale_price) / product.original_price) * 100)
    : null;

  const badgeStyle = getBadgeStyle(product.badge);
  const handleClick = () => navigate(`/reels/${reelId}/${product.id}`);

  return (
    <motion.div
      className="rpp-card"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.08 }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.09, ease: [0.22, 1, 0.36, 1] }}
      onClick={handleClick}
    >
      {/* Image */}
      <div className="rpp-img-wrap">
        <ImageSwiper product={product} />

        {/* Badge — top left */}
        {product.badge && badgeStyle && (
          <span
            className="rpp-badge"
            style={{ background: badgeStyle.bg, color: badgeStyle.color }}
          >
            {product.badge}
          </span>
        )}

        {/* Heart Icon — top right */}
        <button className="rpp-wish-btn"
          onClick={e => { e.stopPropagation(); onToggleWishlist(); }}
          aria-label={wished ? 'Remove from wishlist' : 'Add to wishlist'}>
          <Heart size={13} fill={wished ? '#C0392B' : 'none'} color={wished ? '#C0392B' : '#5C5247'} />
        </button>

        {/* Sold out */}
        {product.stock_qty === 0 && (
          <span className="rpp-soldout">Sold Out</span>
        )}

      </div>

      {/* Body */}
      <div className="rpp-body">
        <h3 className="rpp-name">{product.name}</h3>
        <div className="rpp-prices">
          <span className="rpp-price">{formatPrice(product.sale_price ?? product.original_price)}</span>
          {product.sale_price && (
            <span className="rpp-orig">{formatPrice(product.original_price)}</span>
          )}
          {off > 0 && <span className="rpp-discount">{off}% off</span>}
        </div>
        <button
          className={`rpp-btn-atc${added ? ' added' : ''}`}
          onClick={handleAdd}
          disabled={product.stock_qty === 0}
          style={product.stock_qty === 0 ? { background: '#9A9186', cursor: 'not-allowed' } : {}}
        >
          {added ? '✓ Added to Cart'
            : product.stock_qty === 0 ? 'Sold Out'
            : ([...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length > 1 ||
            [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length > 1) ? 'Select Options'
            : 'Add to Cart'}
        </button>
      </div>
    </motion.div>
  );
};

/* ── Ornament ─────────────────────────────────────────────── */
const Ornament = () => (
  <motion.div
    className="rpp-ornament"
    initial={{ opacity: 0, scaleX: 0.6 }}
    whileInView={{ opacity: 1, scaleX: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.9 }}
  >
    <div className="rpp-ornament-line" />
    <div className="rpp-diamond" />
    <div className="rpp-ornament-line" />
  </motion.div>
);

/* ── Main Page ────────────────────────────────────────────── */
const ReelProductsPage = () => {
  const { reelId } = useParams();
  const navigate = useNavigate();
  const [heroVisible, setHeroVisible] = useState(false);
  const heroRef = useRef(null);

  const [reel,     setReel]     = useState(null);
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState({ show: false, msg: '' });
  const [wishlist, setWishlist] = useState(new Set());

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2200);
  };

  const handleWishlist = async (productId) => {
    if (!getToken()) { navigate('/account'); return; }
    const isIn = wishlist.has(productId);
    setWishlist(prev => {
      const set = new Set(prev);
      isIn ? set.delete(productId) : set.add(productId);
      return set;
    });
    try {
      if (isIn) await removeFromWishlist(productId);
      else await addToWishlist(productId);
    } catch {
      setWishlist(prev => {
        const set = new Set(prev);
        isIn ? set.add(productId) : set.delete(productId);
        return set;
      });
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!reelId) return;
    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      if (getToken()) {
        getWishlist()
          .then(items => setWishlist(new Set(items.map(item => item.id))))
          .catch(() => {});
      }
      
      try {
        const [reelRes, prodsRes] = await Promise.all([
          fetch(`${API}/reels/${reelId}`),
          fetch(`${API}/reels/${reelId}/products`),
        ]);

        if (!reelRes.ok)  throw new Error(`Reel fetch failed: ${reelRes.status}`);
        if (!prodsRes.ok) throw new Error(`Products fetch failed: ${prodsRes.status}`);

        const reelData  = await reelRes.json();
        const prodsData = await prodsRes.json();

        if (reelData.success)  setReel(reelData.reel);
        else                   setError(reelData.error || 'Reel not found');

        if (prodsData.success) setProducts(prodsData.products || []);
        else                   setProducts([]);
      } catch (err) {
        console.error('ReelProductsPage fetch error:', err);
        setError(err.message || 'Failed to connect to server');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reelId]);

  /* ── Loading ── */
  if (loading) return (
    <>
      <style>{pageStyles}</style>
      <div className="rpp-page">
        <div className="rpp-loading">
          <div className="rpp-loading-ring" />
          <p className="rpp-loading-text">Curating the collection…</p>
        </div>
      </div>
    </>
  );

  /* ── Error ── */
  if (error) return (
    <>
      <style>{pageStyles}</style>
      <div className="rpp-page">
        <div className="rpp-error-state">
          <p className="rpp-error-icon">✦</p>
          <h2 className="rpp-error-title">{error}</h2>
          <Link to="/reels" className="rpp-error-back">← Back to Reels</Link>
        </div>
      </div>
    </>
  );

  /* ── Render ── */
  return (
    <>
      <style>{pageStyles}</style>
      <div className="rpp-page">

        {/* ── HERO ── */}
        <div className="rpp-hero" ref={heroRef}>
          <motion.nav
            className="rpp-breadcrumb"
            initial={{ opacity: 0, y: -8 }}
            animate={heroVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45 }}
          >
            <Link to="/reels">Reels</Link>
            <span className="rpp-bc-sep">/</span>
            <span className="rpp-bc-cur">{reel?.title}</span>
          </motion.nav>

          <motion.h1
            className="rpp-hero-title"
            initial={{ opacity: 0, y: 28 }}
            animate={heroVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            {reel?.title}
          </motion.h1>

          <motion.p
            className="rpp-hero-sub"
            initial={{ opacity: 0 }}
            animate={heroVisible ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            {products.length > 0
              ? `${products.length} exclusive piece${products.length !== 1 ? 's' : ''} featured`
              : 'Collection coming soon'}
          </motion.p>

          <motion.div
            className="rpp-hero-divider"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={heroVisible ? { scaleX: 1, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.35 }}
          >
            <span className="rpp-hero-diamond" />
          </motion.div>
        </div>

        {/* ── GRID ── */}
        <div className="rpp-inner">
          {products.length === 0 ? (
            <div className="rpp-empty">
              <p className="rpp-empty-icon">◇</p>
              <div className="rpp-empty-title">No products yet</div>
              <p className="rpp-empty-sub">Check back soon — something beautiful is on its way.</p>
              <Link to="/reels" className="rpp-empty-back">← Browse all reels</Link>
            </div>
          ) : (
            <div className="rpp-grid">
              {products.map((product, i) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  reelId={reelId}
                  index={i}
                  onToast={showToast}
                  wished={wishlist.has(product.id)}
                  onToggleWishlist={() => handleWishlist(product.id)}
                />
              ))}
            </div>
          )}
          <Ornament />
        </div>
      </div>

      <Toast msg={toast.msg} show={toast.show} />
    </>
  );
};

/* ══════════════════════════════════════════════════════════
   STYLES
══════════════════════════════════════════════════════════ */
const pageStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

  .rpp-page {
    background: #FAF8F4;
    min-height: 100vh;
    padding-top: 140px;
    font-family: 'Jost', sans-serif;
    position: relative;
  }
  .rpp-page::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.018'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }

  /* ── Loading ── */
  .rpp-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 20px;
    position: relative;
    z-index: 1;
  }
  .rpp-loading-ring {
    width: 36px;
    height: 36px;
    border: 2px solid #e8e0d5;
    border-top-color: #B08D57;
    border-radius: 50%;
    animation: rppSpin 0.9s linear infinite;
  }
  @keyframes rppSpin { to { transform: rotate(360deg); } }
  .rpp-loading-text {
    font-family: 'Cormorant Garamond', serif;
    font-style: italic;
    font-size: 15px;
    color: #9A9186;
    letter-spacing: 0.08em;
  }

  /* ── Error state ── */
  .rpp-error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 12px;
    position: relative;
    z-index: 1;
    text-align: center;
    padding: 40px;
  }
  .rpp-error-icon  { font-size: 28px; color: #B08D57; margin: 0; }
  .rpp-error-title { font-family: 'Playfair Display', serif; font-size: 22px; color: #1C1A18; margin: 0; }
  .rpp-error-back  { margin-top: 8px; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #B08D57; text-decoration: none; }
  .rpp-error-back:hover { color: #1C1A18; }

  /* ── Hero ── */
  .rpp-hero {
    position: relative;
    z-index: 1;
    text-align: center;
    padding: 56px clamp(20px, 6vw, 100px) 48px;
    background: linear-gradient(170deg, #EDE9E0 0%, #FAF8F4 100%);
    border-bottom: 1px solid rgba(176,141,87,0.18);
  }
  .rpp-breadcrumb {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 9.5px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #9A9186;
    margin-bottom: 22px;
  }
  .rpp-breadcrumb a { color: #B08D57; text-decoration: none; transition: color 0.2s; }
  .rpp-breadcrumb a:hover { color: #1C1A18; }
  .rpp-bc-sep { opacity: 0.4; }
  .rpp-bc-cur { color: #6B6059; }
  .rpp-hero-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(30px, 5.5vw, 62px);
    font-weight: 700;
    color: #1C1A18;
    line-height: 1.04;
    letter-spacing: -0.02em;
    margin: 0 0 10px;
  }
  .rpp-hero-sub {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(14px, 1.6vw, 19px);
    font-style: italic;
    font-weight: 300;
    color: #8A7E72;
    margin: 0 0 32px;
  }
  .rpp-hero-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    transform-origin: center;
  }
  .rpp-hero-divider::before,
  .rpp-hero-divider::after {
    content: '';
    width: 100px;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(176,141,87,0.5));
  }
  .rpp-hero-divider::after { background: linear-gradient(to left, transparent, rgba(176,141,87,0.5)); }
  .rpp-hero-diamond {
    width: 6px; height: 6px;
    background: #B08D57;
    display: inline-block;
    transform: rotate(45deg);
    flex-shrink: 0;
  }

  /* ── Inner / grid ── */
  .rpp-inner {
    position: relative;
    z-index: 1;
    max-width: 1360px;
    margin: 0 auto;
    padding: 56px clamp(16px, 4vw, 64px) 100px;
  }
  .rpp-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 22px;
  }

  /* ── Card ── */
  .rpp-card {
    background: #fff;
    cursor: pointer;
    transition: box-shadow 0.3s, transform 0.25s;
    border: 1px solid rgba(240,235,228,0.8);
  }
  .rpp-card:hover {
    box-shadow: 0 20px 56px rgba(28,26,24,0.11);
    transform: translateY(-4px);
  }
  .rpp-img-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 4;
    overflow: hidden;
    background: #f5f0ea;
  }
  .rpp-slide-wrap {
    width: 100%; height: 100%;
    position: relative;
    overflow: hidden;
  }
  .rpp-slide-strip {
    display: flex;
    width: 100%; height: 100%;
    transition: transform 0.35s ease;
  }
  .rpp-slide-strip img {
    flex: 0 0 100%;
    width: 100%; height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
  .rpp-sarrow {
    position: absolute;
    top: 50%; transform: translateY(-50%);
    width: 30px; height: 30px;
    border: none; border-radius: 50%;
    background: rgba(255,255,255,0.9);
    color: #1C1A18; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    z-index: 4; opacity: 0;
    transition: opacity 0.2s, background 0.2s;
  }
  .rpp-img-wrap:hover .rpp-sarrow,
  .rpp-card:hover .rpp-sarrow { opacity: 1; }
  .rpp-sarrow:hover { background: #fff; }
  .rpp-sarrow-l { left: 10px; }
  .rpp-sarrow-r { right: 10px; }

  .rpp-badge {
    position: absolute;
    top: 10px; left: 10px;
    font-family: 'Jost', sans-serif;
    font-size: 8.5px; font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 5px 10px;
    z-index: 3;
  }
  .rpp-wish-btn {
    position: absolute;
    top: 9px; right: 9px;
    z-index: 3;
    width: 30px; height: 30px;
    background: rgba(250,248,244,0.9);
    border: none; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
  }
  .rpp-discount {
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #C0392B;
    background: rgba(192,57,43,0.08);
    padding: 2px 5px;
  }
  .rpp-overlay {
    position: absolute;
    inset: 0;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;
    z-index: 4;
    pointer-events: none;
  }
  .rpp-card:hover .rpp-overlay { opacity: 1; }
  .rpp-soldout {
    position: absolute; bottom: 0; left: 0; right: 0;
    background: rgba(28,26,24,0.82);
    color: #fff;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 6px;
    text-align: center;
    z-index: 5;
  }
  .rpp-quick-view {
    font-family: 'Jost', sans-serif;
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #1C1A18;
    background: #fff;
    padding: 13px 32px;
    transition: background 0.2s, color 0.2s;
  }
  .rpp-quick-view:hover { background: #B08D57; color: #fff; }

  /* ── Card body ── */
  .rpp-body {
    padding: 15px 16px 18px;
    border-top: 1px solid #f5f0e8;
  }
  .rpp-name {
    font-family: 'Playfair Display', serif;
    font-size: 14.5px; font-weight: 600;
    color: #1C1A18;
    margin: 0 0 9px;
    line-height: 1.3;
  }
  .rpp-prices {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 13px;
    flex-wrap: wrap;
  }
  .rpp-price { font-size: 15px; font-weight: 700; color: #B08D57; }
  .rpp-orig  { font-size: 12px; color: #AEA39A; text-decoration: line-through; font-weight: 300; }

  /* ── Add to Cart button ── */
  .rpp-btn-atc {
    display: block;
    width: 100%;
    background: #1C1A18;
    color: #fff;
    font-family: 'Jost', sans-serif;
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    padding: 13px;
    text-align: center;
    border: none;
    cursor: pointer;
    transition: background 0.22s;
  }
  .rpp-btn-atc:hover { background: #B08D57; }
  .rpp-btn-atc.added { background: #B08D57; }

  /* ── Toast ── */
  .rpp-toast {
      position: fixed;
      bottom: 80px;
      left: 20px;
      right: 20px;
      width: auto;
      background: #1C1A18;
      color: #FAF8F4;
      font-family: 'Jost', sans-serif;
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.1em;
      padding: 12px 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      z-index: 9999;
      box-shadow: 0 8px 32px rgba(0,0,0,0.22);
      text-align: center;
    }

  /* ── Empty state ── */
  .rpp-empty {
    text-align: center;
    padding: 100px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .rpp-empty-icon  { font-size: 32px; color: rgba(176,141,87,0.5); margin: 0; }
  .rpp-empty-title { font-family: 'Playfair Display', serif; font-size: 24px; color: #3a3530; margin: 0; }
  .rpp-empty-sub   { font-size: 13px; color: #9A9186; margin: 0; }
  .rpp-empty-back  { margin-top: 8px; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #B08D57; text-decoration: none; }
  .rpp-empty-back:hover { color: #1C1A18; }

  /* ── Ornament ── */
  .rpp-ornament {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    margin-top: 72px;
  }
  .rpp-ornament-line {
    width: 90px; height: 1px;
    background: linear-gradient(to right, transparent, rgba(176,141,87,0.5));
  }
  .rpp-ornament-line:last-child {
    background: linear-gradient(to left, transparent, rgba(176,141,87,0.5));
  }
  .rpp-diamond {
    width: 6px; height: 6px;
    background: #B08D57;
    transform: rotate(45deg);
    flex-shrink: 0;
  }

  /* ── Responsive ── */
  @media (max-width: 1100px) { .rpp-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 768px) {
    .rpp-page  { padding-top: 105px; }
    .rpp-hero  { padding: 40px 20px 36px; }
    .rpp-grid  { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .rpp-inner { padding: 36px 14px 60px; }
  }
  @media (max-width: 420px) {
    .rpp-grid { gap: 10px; }
    .rpp-body { padding: 12px 12px 14px; }
  }
  @media (hover: none) {
    .rpp-btn-atc::after { display: none; }
    .rpp-btn-atc:active { background: #B08D57; transition: background 0.1s; }
  }
`;

export default ReelProductsPage;